import {PropertyList, PropertyListItem, view, Button} from '@sail/ui';
import {defineMessages, FormattedMessage} from 'react-intl';
import {widgetLinkAction} from '@stripe-internal/conversations-ui';
import {BankAccountWithIcon} from '../helpers/BankAccountWithIcon';
import {Audience, Payout} from './MerchantPayoutStatus';
import {formatUtcDate} from './date';

const messages = defineMessages({
  paymentAmountTitle: {
    id: 'mse_inline_data.merchant_payout_status.payment_amount_title',
    description: 'Header displayed above the payment amount.',
    defaultMessage: 'Amount',
  },
  paymentMethodTitle: {
    id: 'mse_inline_data.merchant_payout_status.payment_method_title',
    description: 'Header displayed above bank account or card number',
    defaultMessage: 'Bank account or card',
  },
  expectedDepositDateTitle: {
    id: 'mse_inline_data.merchant_payout_status.expected_deposit_date_title',
    description:
      'Header displayed above expected deposit date for non-complete and non-failed payouts',
    defaultMessage: 'Expected completion date',
  },
  postDateTitle: {
    id: 'mse_inline_data.merchant_payout_status.post_deposit_date_title',
    description:
      'Header displayed above payout post date when payout is complete',
    defaultMessage: 'Payout completed date',
  },
  failedDepositDateTitle: {
    id: 'mse_inline_data.merchant_payout_status.date_deposit_failed_title',
    description: 'Header displayed above payout failed date when payout failed',
    defaultMessage: 'Payout failed date',
  },
  viewPayoutButtonDescription: {
    id: 'mse_inline_data.merchant_payout_status.view_payout_button_description',
    description: 'View Payout button description',
    defaultMessage: 'View payout',
  },
});

const payoutButtonLink = (payout: Payout, audience?: Audience) =>
  audience === 'express'
    ? 'https://connect.stripe.com/app/express'
    : `https://dashboard.stripe.com/payouts/${payout.token}`;

export const PayoutDetailsSection = ({
  payout,
  audience,
}: {
  payout: Payout;
  audience?: Audience;
}) => {
  const localizedDate = formatUtcDate(payout.date);

  return (
    <view.div css={{stack: 'y', gap: 'medium'}}>
      <PropertyList>
        <PropertyListItem
          label={
            <view.div css={{font: 'label.medium.emphasized'}}>
              <FormattedMessage {...messages.paymentAmountTitle} />
            </view.div>
          }
          value={
            <view.div
              css={{
                stack: 'x',
                gap: 'xsmall',
                alignX: 'start',
                alignY: 'center',
              }}
            >
              <view.span css={{font: 'heading.medium'}}>
                {payout.amount}
              </view.span>
              <view.span
                css={{font: 'heading.medium.subdued', textColor: 'subdued'}}
              >
                {payout.currency.toLocaleUpperCase()}
              </view.span>
            </view.div>
          }
        />
        {payout.destination ? (
          <PropertyListItem
            label={
              <view.div css={{font: 'label.medium.emphasized'}}>
                <FormattedMessage {...messages.paymentMethodTitle} />
              </view.div>
            }
            value={
              <BankAccountWithIcon
                iconName={payout.destination?.brand_icon}
                accountName={payout.destination?.text}
              />
            }
          />
        ) : null}
        <PropertyListItem
          label={
            <view.div css={{font: 'label.medium.emphasized'}}>
              {/* eslint-disable no-nested-ternary */}
              {payout.status === 'paid' ? (
                <FormattedMessage {...messages.postDateTitle} />
              ) : payout.status === 'failed' ? (
                <FormattedMessage {...messages.failedDepositDateTitle} />
              ) : (
                <FormattedMessage {...messages.expectedDepositDateTitle} />
              )}
            </view.div>
          }
          value={localizedDate}
        />
      </PropertyList>
      <Button
        data-testid="cta-button"
        type="primary"
        css={{width: 'fill', size: 'small'}}
        uses={[widgetLinkAction()]}
        href={payoutButtonLink(payout, audience)}
      >
        <FormattedMessage {...messages.viewPayoutButtonDescription} />
      </Button>
    </view.div>
  );
};
